<template>
  <div>
    <div class="tillbakalank">
      <button class="prevBtn btn-link" type="button" @click="$emit('back')">
        <svg-icon icon-name="pil-vanster-ny" />
        <span>Tillbaka till översikten</span>
      </button>
    </div>

    <div v-if="actionError" class="alert alert-danger alert-dismissible" role="alert">
      <button type="button" class="close" @click="$emit('dismiss-error')" aria-label="Stäng">
        <span aria-hidden="true">&times;</span>
      </button>
      <strong>Något gick fel.</strong> {{ actionError }}
    </div>

    <h1 class="rubrik-steg">Förhandsvisa tips</h1>

    <div class="tipslista__kort col-md-8">
      <HittaTipsSidaKort :tips="kortTips" />
    </div>

    <div class="forhandsvisning-info col-md-4">
      <h2>Information kring tipset</h2>
      <div>
        <span class="forhandsvisning-info--fettext">Status:</span>
        <span :class="['forhandsvisning-info--status', tips.Status]">{{ statusLabel(tips.Status) }}</span>
      </div>
      <template v-if="tips.Status !== 'Nyinkommet'">
        <div>
          <span class="forhandsvisning-info--fettext">{{ isPublished ? 'Publicerad av:' : 'Granskare:' }}</span>
          {{ tips.SenastÄndradAv }}
        </div>
        <template v-if="isPublished">
          <div><span class="forhandsvisning-info--fettext">Senast publicerat:</span> {{ tips.PubliceradDatumTid }}</div>
          <div><span class="forhandsvisning-info--fettext">Utgångsdatum:</span> {{ tips.UtgårDatum }}</div>
        </template>
      </template>
      <h3>Kontaktuppgifter till tipsare</h3>
      <div class="forhandsvisning-info--kontaktuppgifter">
        <span class="forhandsvisning-info--fettext">Namn:</span> {{ tips.InskickadAvNamn }}
      </div>
      <div class="forhandsvisning-info--kontaktuppgifter">
        <span class="forhandsvisning-info--fettext">E-postadress:</span> {{ tips.InskickadAvEpost }}
      </div>
      <template v-if="isPublished && tips.EpiserverEditUrl">
        <h3>Redigera tips i Episerver</h3>
        <a :href="tips.EpiserverEditUrl" target="_blank" rel="noopener noreferrer" class="redigering--lank">
          Länk till tipset i Episerver
          <span class="sr-only">, öppna i ny flik</span>
        </a>
      </template>
    </div>

    <div class="forhandsvisning-knapp-container col-md-8">
      <div class="forhandsvisning-knappar">
        <template v-if="tips.Id === 0">
          <button class="btn btn-default" type="button" @click="showDeleteConfirm = true">Ta bort</button>
          <button class="btn btn-default" type="button" @click="$emit('publish', tips)">Publicera</button>
          <button class="btn btn-primary" type="button" @click="$emit('edit', tips)">Ändra</button>
        </template>
        <template v-else-if="isPublished">
          <button class="btn btn-default" type="button" @click="showUnpublishConfirm = true">Avpublicera</button>
          <button v-if="tips.Status === 'UtgårSnart'" class="btn btn-default" type="button" @click="$emit('update-date', tips)">Uppdatera utgångsdatum</button>
          <button class="btn btn-primary" type="button" @click="$emit('edit', tips)">Ändra</button>
        </template>
        <template v-else>
          <button class="btn btn-default" type="button" @click="showDeleteConfirm = true">Ta bort</button>
          <button class="btn btn-default" type="button" @click="$emit('publish', tips)">Publicera</button>
          <button class="btn btn-primary" type="button" @click="$emit('edit', tips)">Ändra</button>
        </template>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <confirm-modal
      v-if="showDeleteConfirm"
      title="Vill du ta bort tipset?"
      confirm-label="Ja, ta bort tipset"
      cancel-label="Nej, behåll tipset"
      @confirm="confirmDelete"
      @close="showDeleteConfirm = false"
    >
      Är du säker på att du vill ta bort tipset? Tipset kommer då att försvinna och inte finnas kvar.
    </confirm-modal>

    <!-- Unpublish confirmation modal -->
    <confirm-modal
      v-if="showUnpublishConfirm"
      title="Vill du avpublicera tipset?"
      confirm-label="Ja, avpublicera tipset"
      cancel-label="Nej, behåll tipset"
      @confirm="confirmUnpublish"
      @close="showUnpublishConfirm = false"
    >
      Är du säker på att du vill avpublicera tipset? Tipset kommer då inte att visas för besökare.
      Tipset kommer att finnas kvar i 3 månader och du kan då ompublicera det igen om du skulle vilja det.
    </confirm-modal>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref } from 'vue';
import HittaTipsSidaKort from './HittaTipsSidaKort.vue';
import SvgIcon from '../components/svgicon.vue';
import ConfirmModal from '../components/ConfirmModal.vue';
import { TipsItemDto } from './hittaTipsSidaService';
import { HanteraTipsPreviewDto } from './hanteraTipsSidaService';

const statusLabels: Record<string, string> = {
  Nyinkommet:   'Nyinkommet',
  Granskas:     'Granskas',
  Publicerat:   'Publicerat',
  Kompletteras: 'Kompletteras',
  Avpublicerat: 'Avpublicerat',
  UtgårSnart:   'Utgår snart',
};

export default defineComponent({
  name: 'HanteraTipsPreview',
  components: { HittaTipsSidaKort, SvgIcon, ConfirmModal },
  props: {
    tips: { type: Object as PropType<HanteraTipsPreviewDto>, required: true },
    actionError: { type: String as PropType<string | null>, default: null },
  },
  emits: ['back', 'delete', 'publish', 'unpublish', 'update-date', 'edit', 'dismiss-error'],
  setup(props, { emit }) {
    const isPublished = computed(() =>
      props.tips.Status === 'Publicerat' || props.tips.Status === 'UtgårSnart'
    );

    const kortTips = computed<TipsItemDto>(() => ({
      Id: props.tips.Id,
      Rubrik: props.tips.Rubrik,
      Ingress: props.tips.Ingress,
      Beskrivning: props.tips.BeskrivningHtml,
      Framtagare: props.tips.Framtagare,
      LankMaterial: props.tips.LänkMaterial,
      InskickadDatumTid: props.tips.InskickadDatumTid,
      DelningsUrl: '',
      MarkeratTips: true,
      Nyckelord: props.tips.Nyckelord.map(t => ({ Name: t.Name, Chosen: false })),
      Typer: props.tips.Typer.map(t => ({ Name: t.Name, Chosen: false })),
    }));

    const showDeleteConfirm = ref(false);
    const showUnpublishConfirm = ref(false);

    function statusLabel(status: string): string {
      return statusLabels[status] ?? status;
    }

    function confirmDelete() {
      showDeleteConfirm.value = false;
      emit('delete', props.tips);
    }

    function confirmUnpublish() {
      showUnpublishConfirm.value = false;
      emit('unpublish', props.tips);
    }

    return { isPublished, kortTips, statusLabel, showDeleteConfirm, showUnpublishConfirm, confirmDelete, confirmUnpublish };
  },
});
</script>
